import {
  Box,
  Button,
  Center,
  Divider,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Slide,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { Plural, Trans, t } from "@lingui/macro";
import { Icon } from "@src/components/ui-kit/Icon";
import { observer } from "mobx-react-lite";
import type {
  BulkAction,
  BulkActionsProps,
  BulkActionTextState,
  BulkMenuAction,
} from "./types";

function resolveBooleanValue(value?: boolean | (() => boolean)): boolean {
  if (typeof value === "function") {
    return value();
  }

  return Boolean(value);
}

function resolveTextValue(value?: BulkActionTextState): string | undefined {
  if (typeof value === "function") {
    return value();
  }

  return value;
}

function hasSubActions(action: BulkAction): action is BulkMenuAction {
  return "subActions" in action;
}

export const BulkActions = observer(function BulkActions({
  store,
  infoBlock,
}: BulkActionsProps) {
  if (store.selectedCount === 0) return null;

  return (
    <Slide
      direction="bottom"
      in={store.selectedCount > 0}
      style={{ zIndex: 10, pointerEvents: "none" }}
    >
      <Center pos="absolute" right="0" bottom="2" left="0" pointerEvents="none">
        <Box
          p="4"
          bg="white"
          border="1px"
          borderColor="gray.200"
          shadow="lg"
          pointerEvents="auto"
          rounded="lg"
        >
          <HStack
            align="center"
            justify="space-between"
            divider={<Divider h="6" orientation="vertical" />}
            spacing="4"
          >
            <HStack spacing="4">
              <Text fontWeight="medium">
                <Plural
                  value={store.selectedCount}
                  one="# item selected"
                  other="# items selected"
                />
              </Text>

              {store.showSelectAllButton && (
                <Button
                  leftIcon={<Icon name="check" />}
                  onClick={store.handleSelectAll}
                  size="sm"
                  variant="ghost"
                >
                  <Trans>Select all</Trans>&nbsp;{store.allIds.length}&nbsp;
                  <Trans>items</Trans>
                </Button>
              )}
            </HStack>

            {infoBlock && <Box>{infoBlock}</Box>}

            {store.actions.length > 0 && (
              <HStack spacing="2">
                {store.actions.map((action) => {
                  const isActionDisabled = resolveBooleanValue(
                    action.isDisabled,
                  );
                  const isActionLoading = resolveBooleanValue(action.isLoading);

                  if (hasSubActions(action)) {
                    return (
                      <Menu key={action.id}>
                        <Tooltip label={action.label()} placement="top">
                          <MenuButton
                            as={IconButton}
                            aria-label={action.label()}
                            colorScheme={action.iconColorScheme}
                            icon={<Icon name={action.icon} />}
                            isDisabled={isActionDisabled}
                            isLoading={isActionLoading}
                            size="sm"
                            title={action.label()}
                            variant="ghost"
                          />
                        </Tooltip>
                        <MenuList>
                          {action.subActions.map((subAction) => {
                            const isSubActionDisabled = resolveBooleanValue(
                              subAction.isDisabled,
                            );
                            const isSubActionDestructive = resolveBooleanValue(
                              subAction.isDestructive,
                            );
                            const tooltip = resolveTextValue(subAction.tooltip);

                            return (
                              <Tooltip
                                key={subAction.id}
                                hasArrow
                                isDisabled={!isSubActionDisabled || !tooltip}
                                label={tooltip}
                                placement="right"
                              >
                                <Box>
                                  <MenuItem
                                    color={
                                      isSubActionDestructive
                                        ? "red.500"
                                        : undefined
                                    }
                                    isDisabled={isSubActionDisabled}
                                    onClick={() =>
                                      subAction.callback(store.selectedIdsArray)
                                    }
                                  >
                                    {subAction.label()}
                                  </MenuItem>
                                </Box>
                              </Tooltip>
                            );
                          })}
                        </MenuList>
                      </Menu>
                    );
                  }

                  return (
                    <Tooltip
                      key={action.id}
                      label={action.label()}
                      placement="top"
                    >
                      <IconButton
                        aria-label={action.label()}
                        colorScheme={action.iconColorScheme}
                        icon={<Icon name={action.icon} />}
                        isDisabled={isActionDisabled}
                        isLoading={isActionLoading}
                        onClick={() => action.callback(store.selectedIdsArray)}
                        size="sm"
                        title={action.label()}
                        variant="ghost"
                      />
                    </Tooltip>
                  );
                })}
              </HStack>
            )}

            <IconButton
              aria-label={t`Cancel`}
              icon={<Icon name="x-close" />}
              onClick={store.handleCancelSelection}
              size="sm"
              variant="ghost"
            />
          </HStack>
        </Box>
      </Center>
    </Slide>
  );
});
